/**
 * Public API of @/shared/storage.
 *
 * This is the ONLY allowed entry point for client-side persistent state.
 * Direct `localStorage.` and `sessionStorage.` access elsewhere is
 * forbidden by ESLint (`no-restricted-syntax`).
 *
 * Re-exports:
 *   - tokenMemory      : in-memory access token store (auth subsystem only)
 *   - storage          : namespaced localStorage (prefs, ui, draft)
 *   - sessionStore     : namespaced sessionStorage (flow)
 *   - broadcastLogin   : cross-tab login sync
 *   - broadcastLogout  : cross-tab logout sync
 *   - StorageGuardError: thrown by safeSet when a credential-like key is rejected
 *   - clearAllUserScoped: one-shot user-state cleanup (logout flow)
 *
 * See:
 *   - docs/adr/0009-storage-architecture-and-token-strategy.md
 *   - docs/api-contracts/auth.md
 */

import { tokenMemory } from './memory'
import { localStore } from './localStorage'
import { sessionStore } from './session'

export { tokenMemory } from './memory'
export { localStore as storage, StorageGuardError } from './localStorage'
export { sessionStore } from './session'
export { broadcastLogout, broadcastLogin } from './cross-tab'

/**
 * Wipe all user-scoped client state. Called by the logout flow and by the
 * cross-tab logout broadcast handler.
 *
 * Clears:
 *   - in-memory access token (tokenMemory)
 *   - user-scoped localStorage (drafts; preserves prefs and ui)
 *   - all sessionStorage (per-tab flow state, plus infrastructure keys
 *     which are safe to drop on logout)
 *
 * Does NOT call queryClient.clear() — that lives in the auth subsystem
 * (src/shared/api/auth.ts:logout) so this module stays free of TanStack
 * Query knowledge for testability.
 */
export function clearAllUserScoped(): void {
  tokenMemory.clear()
  localStore.clearUserScoped()
  sessionStore.clear()
}
